/**
 * Path-mode global state.
 *
 * Path-mode is a *map state* — distinct from the tool that enters it. The user has
 * selected masts along a shared line with the `mastpointsAlongLine` tool and finished
 * it with a right click, and the map is now in a mode where the user can inspect
 * sections, toggle load cases, run the section calculation, etc. This module owns the
 * canonical state for that mode.
 *
 * Lives in a `.svelte.ts` module so we can use Svelte 5 runes (`$state`,
 * `$derived`) at module scope. Components import `pathModeState` and read
 * properties directly — Svelte's compiler wires up fine-grained reactivity
 * automatically:
 *
 *     import { pathModeState } from '…/path-mode-state.svelte';
 *     const isActive = $derived(pathModeState.active);
 *
 * Non-rune modules (plain `.ts`) can't read `$state` reactively. If a non-UI
 * module needs to react to state changes, convert it to `.svelte.ts` and use
 * `$effect.root(() => $effect(() => …))` for module-lifetime subscriptions.
 */

import { get as idbGet, set as idbSet } from 'idb-keyval';

import type { ISectionInfo } from '../map-tools-store';
import { broadcastSectionCalculationStale } from '$lib/stores/polygon-section-stale-store';
import { POLYGON_SECTION_CALCULATION_IDB_KEY } from './pathToolUtils';

class PathModeState {
	/** True once a path is finished and path-mode is the active map state. */
	active = $state(false);

	/** Ids of the masts selected with the tool, in order. Sent to `/info` and `/calculate`. */
	mastIds = $state<number[]>([]);

	/** Section info from the `/info` (and `/calculate`) endpoint. */
	sectionInfo = $state<ISectionInfo | null>(null);
	sectionInfoLoading = $state(false);
	sectionInfoError = $state<string | null>(null);

	/** Names of load cases currently visualised on the map. */
	activeLoadCases = $state<string[]>([]);

	/**
	 * Hover shows the worst stored load case per component - poles by largest calculated JBD,
	 * everything else by largest utilization - instead of only the active case. The map geometry
	 * is still driven by `activeLoadCases`; this is a value choice for the tooltip, not new
	 * geometry. Switched off automatically when the user ticks a single case.
	 */
	worstCaseMode = $state(true);

	/**
	 * Which backend endpoint last populated `sectionInfo.loadCases`:
	 *   - `'info'` — stiff structural calculation (`Section/calculate/info`).
	 *   - `'calculate'` — full DSS run (`/calculate`).
	 *   - `null` — no section info yet, or it has been cleared.
	 *
	 * Drives the small label above the "Lasttilfeller" pill so users know
	 * which calculation produced the values they're toggling between.
	 */
	loadCaseSource = $state<'info' | 'calculate' | null>(null);

	/**
	 * The dimensioning calculation (`/calculate`) for this path:
	 *   - `'none'` — not run yet.
	 *   - `'fresh'` — run, and nothing in the section has changed since.
	 *   - `'stale'` — run, but the infrastructure has changed since, so the results are outdated.
	 * Set to `'fresh'` by `applyRecalculatedSectionInfo` and the panel's run, and to `'stale'` by
	 * `markSectionCalculationCacheStale`, which every section-changing edit goes through.
	 */
	calculationStatus = $state<'none' | 'fresh' | 'stale'>('none');

	/** Toggles visibility of `LineTerrain` measurement entities. */
	showDistances = $state(false);

	/**
	 * Line colouring mode for the section panel:
	 *   - `'section'` — every line painted with its section-circuit colour.
	 *   - `'loadcase'` — load-case duplicates painted with the load case's
	 *     own `rgb`; plan/live originals (visible only when no active load
	 *     case covers them) keep the section-circuit colour.
	 */
	colorMode = $state<'section' | 'loadcase'>('section');

	/**
	 * When `true`, the right-side panel is collapsed to a small floating chip.
	 * When `false`, the full `PathModeDetails` panel is shown.
	 */
	panelMinimized = $state(false);

	// ── Derived ────────────────────────────────────────────────────────────

	readonly loadCases = $derived(this.sectionInfo?.loadCases ?? []);

	// ── Mutators ───────────────────────────────────────────────────────────

	/** Minimises the right-side panel to the chip. */
	minimize(): void {
		this.panelMinimized = true;
	}

	/** Restores the right-side panel from chip. */
	maximize(): void {
		this.panelMinimized = false;
	}

	/** Resets every field to its default — called by `deactivatePathMode`. */
	reset(): void {
		this.active = false;
		this.mastIds = [];
		this.sectionInfo = null;
		this.sectionInfoLoading = false;
		this.sectionInfoError = null;
		this.activeLoadCases = [];
		this.worstCaseMode = true;
		this.loadCaseSource = null;
		this.calculationStatus = 'none';
		this.showDistances = false;
		this.colorMode = 'section';
		this.panelMinimized = false;
	}
}

export const pathModeState = new PathModeState();

/**
 * Mark the IDB-cached section-calculation payload as stale and broadcast the
 * flag to any open `/reports/polygon-section-calculation` tab. Called whenever
 * an action changes underlying entities (sub-tool edits, mutation responses,
 * montagestretch saves) so the report banner can prompt regeneration.
 *
 * Lives here (not in `pathMode.ts`) so `map-utils.ts` can import it without
 * creating a circular dependency through `pathMode.ts → map-utils.ts`.
 */
export async function markSectionCalculationCacheStale(): Promise<void> {
	if (pathModeState.calculationStatus === 'fresh') pathModeState.calculationStatus = 'stale';

	// Push the flag to any open report tab via BroadcastChannel before writing
	// to IDB — `idb-keyval` doesn't emit cross-tab events, so without the
	// broadcast a report tab that's already mounted wouldn't pick up the
	// new stale state until reload.
	broadcastSectionCalculationStale();

	const cached = (await idbGet(POLYGON_SECTION_CALCULATION_IDB_KEY)) as
		| Record<string, unknown>
		| undefined;
	if (!cached) return;
	await idbSet(POLYGON_SECTION_CALCULATION_IDB_KEY, { ...cached, stale: true });
}
